/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { FaPhone, FaPhoneSlash, FaTimes, FaMicrophone, FaMicrophoneSlash } from 'react-icons/fa';
import { MdPhoneLocked, MdWarning } from 'react-icons/md';
import { BsFillTelephoneMinusFill } from 'react-icons/bs';
// A .ts module; the repo's eslint import resolver only knows js/jsx.
// eslint-disable-next-line import/no-unresolved, import/extensions
import { secondsLeft } from './transfer/transfer_state';
import StudentProfileLink from './StudentProfileLink';
import './outbound_call_pill.css';

const TERMINAL_STATUSES = new Set(['ended', 'disconnected', 'failed', 'rejected']);

const formatDuration = (totalSeconds) => {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');
  return `${mm} min, ${ss} sec`;
};

const STATUS_LABEL = {
  ringing: 'Incoming call…',
  connecting: 'Connecting…',
  connected: 'Connected',
  reconnecting: 'Reconnecting…',
  disconnecting: 'Ending…',
  ended: 'Call ended',
  rejected: 'Rejected',
  disconnected: 'Disconnected',
  failed: 'Call failed'
};

const STATUS_COLOR = {
  ringing: 'var(--state-warning)',
  connecting: 'var(--text-muted)',
  connected: 'var(--state-success)',
  reconnecting: 'var(--state-warning)',
  disconnecting: 'var(--text-muted)',
  ended: 'var(--text-muted)',
  rejected: 'var(--text-muted)',
  disconnected: 'var(--text-muted)',
  failed: 'var(--state-error)'
};

function IncomingCallPill({
  fromNumber,
  studentName,
  studentUuid = null, // set when the number matched a student; shows "Open profile" once connected
  status,
  muted,
  // Non-null while a media problem is active on this leg.
  audioWarning = null,
  // Multi-agent / conference state. All optional — pre-feature pills work as before.
  unlocked = false, // true once the call has been moved into a Conference
  isOriginator = true, // true on the pill of the ops user who answered/initiated
  participantCount = 1, // number of ops users currently in the conference (cms_*)
  // Warm transfer (PROD-10258), both optional: B's ring details, and A's Transfer control.
  transferInvite = null, // { fromAgentName, note, expiresAt } while B is being rung
  transferSlot = null,
  onAccept,
  onReject,
  onToggleMute,
  onEndCall, // pre-unlock: disconnect SDK call (today's behavior)
  onUnlock, // originator only: POST /unlock_call to upgrade to conference
  onLeave, // post-unlock + 2+ ops: disconnect own leg, conference continues
  onEndForEveryone, // post-unlock: POST /end_ongoing then disconnect
  onClose
}) {
  const [elapsed, setElapsed] = useState(0);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (!transferInvite || status !== 'ringing') return undefined;
    const id = setInterval(() => setNowMs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [transferInvite, status]);

  useEffect(() => {
    // Reset the timer whenever a fresh call cycle begins. Without this the
    // pill — which can outlive a single call if the agent hasn't clicked the
    // close button when the next call comes in — would continue from the
    // previous call's elapsed value once the new one connects.
    if (status === 'ringing' || status === 'connecting') {
      setElapsed(0);
    }
    if (status !== 'connected') return undefined;
    const id = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [status]);

  const statusLabel = STATUS_LABEL[status] || status;
  const statusColor = STATUS_COLOR[status] || 'var(--text-muted)';
  // A reconnecting leg is still the agent's live call, so it keeps the mute /
  // end / conference controls rather than falling back to the bare pill.
  const liveControls = status === 'connected' || status === 'reconnecting';

  return (
    <div
      className="ocp-pill"
      style={{
        zIndex: 9999,
        background: 'var(--surface-card)',
        boxShadow: '0 6px 20px rgba(0, 0, 0, 0.15)',
        padding: '8px 8px 8px 20px',
        display: 'flex',
        alignItems: 'center',
        gap: '14px',
        border: '1px solid var(--border-default)'
      }}
    >
      <div
        className="ocp-info"
        style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2, gap: '6px' }}
      >
        <span
          className="ocp-ellipsis"
          style={{ fontWeight: 600, fontSize: '14px', color: 'var(--text-primary)' }}
        >
          {studentName || fromNumber || 'Unknown caller'}
        </span>
        {studentName && fromNumber && (
          <span className="ocp-ellipsis" style={{ fontSize: '11px', color: 'var(--text-muted)' }}>
            {fromNumber}
          </span>
        )}
        {liveControls && <StudentProfileLink studentUuid={studentUuid} />}
        <span
          className="ocp-status"
          style={{
            fontSize: '12px',
            color: statusColor,
            display: 'flex',
            gap: '6px',
            alignItems: 'center'
          }}
        >
          <span
            style={{
              display: 'inline-block',
              width: '6px',
              height: '6px',
              borderRadius: '9999px',
              background: statusColor
            }}
          />
          {statusLabel}
          {status === 'connected' && (
            <span className="ocp-timer" style={{ color: 'var(--text-muted)' }}>
              <span className="ocp-timer-sep">· </span>
              {formatDuration(elapsed)}
            </span>
          )}
        </span>
        {transferInvite && status === 'ringing' && (
          <span style={{ fontSize: '12px', color: 'var(--text-secondary)', maxWidth: '260px' }}>
            Transfer from {transferInvite.fromAgentName || 'a colleague'} ·{' '}
            {secondsLeft(transferInvite.expiresAt, nowMs)}s
            {transferInvite.note && (
              <span style={{ display: 'block', color: 'var(--text-muted)' }}>
                “{transferInvite.note}”
              </span>
            )}
          </span>
        )}
        {audioWarning && (
          <span
            title={audioWarning}
            style={{
              fontSize: '11px',
              fontWeight: 600,
              color: 'var(--state-error-text)',
              background: 'var(--state-error-bg)',
              borderRadius: '9999px',
              padding: '2px 8px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '4px',
              maxWidth: '260px'
            }}
          >
            <MdWarning size={12} />
            Audio issue
          </span>
        )}
      </div>
      {status === 'ringing' && (
        <div className="ocp-actions" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            type="button"
            onClick={onReject}
            aria-label="Reject"
            title="Reject"
            style={{
              background: 'var(--state-error)',
              color: 'var(--text-inverse)',
              border: 'none',
              borderRadius: '9999px',
              padding: '8px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 500
            }}
          >
            <FaPhoneSlash size={14} style={{ transform: 'rotate(90deg)' }} />
            Reject
          </button>
          <button
            type="button"
            onClick={onAccept}
            aria-label="Accept"
            title="Accept"
            style={{
              background: 'var(--state-success)',
              color: 'var(--text-inverse)',
              border: 'none',
              borderRadius: '9999px',
              padding: '8px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 500
            }}
          >
            <FaPhone size={14} style={{ transform: 'rotate(90deg)' }} />
            Accept
          </button>
        </div>
      )}
      {liveControls && (
        <div className="ocp-actions" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            type="button"
            onClick={onToggleMute}
            aria-label={muted ? 'Unmute' : 'Mute'}
            title={muted ? 'Unmute' : 'Mute'}
            style={{
              width: '36px',
              height: '36px',
              borderRadius: '9999px',
              background: muted ? 'var(--state-error-bg)' : 'var(--surface-sunken)',
              color: muted ? 'var(--state-error-text)' : 'var(--text-secondary)',
              border: 'none',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              padding: 0,
              flexShrink: 0
            }}
          >
            {muted ? <FaMicrophoneSlash size={16} /> : <FaMicrophone size={16} />}
          </button>

          {/* Originator-only: while the call is private, offer to upgrade it to a
              conference so other ops users can join. One-shot — once unlocked
              the icon disappears and this pill switches to conference controls. */}
          {isOriginator && !unlocked && onUnlock && (
            <button
              type="button"
              onClick={onUnlock}
              aria-label="Move call to conference"
              title="Move this call to a conference so other ops users can join"
              style={{
                width: '36px',
                height: '36px',
                borderRadius: '9999px',
                background: 'var(--state-warning-bg)',
                color: 'var(--state-warning-text)',
                border: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                padding: 0,
                flexShrink: 0
              }}
            >
              <MdPhoneLocked size={18} />
            </button>
          )}

          {transferSlot}

          {/* Conference mode + 2+ ops users: drop only this leg; call continues. */}
          {unlocked && participantCount > 1 && onLeave && (
            <button
              type="button"
              onClick={onLeave}
              aria-label="Leave call"
              title="Leave the call (continues for everyone else)"
              style={{
                background: 'var(--surface-sunken)',
                color: 'var(--text-secondary)',
                border: '1px solid var(--border-default)',
                borderRadius: '9999px',
                padding: '8px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: 500
              }}
            >
              <BsFillTelephoneMinusFill size={14} />
              <span className="ocp-btn-label">Leave</span>
            </button>
          )}

          <button
            type="button"
            onClick={unlocked && onEndForEveryone ? onEndForEveryone : onEndCall}
            aria-label={unlocked ? 'End call for everyone' : 'End call'}
            title={unlocked ? 'End call for everyone' : 'End call'}
            style={{
              background: 'var(--state-error)',
              color: 'var(--text-inverse)',
              border: 'none',
              borderRadius: '9999px',
              padding: '8px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: 500
            }}
          >
            <FaPhoneSlash size={14} style={{ transform: 'rotate(90deg)' }} />
            <span className="ocp-btn-label">{unlocked ? 'End for everyone' : 'End call'}</span>
          </button>
        </div>
      )}
      {TERMINAL_STATUSES.has(status) && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          style={{
            marginLeft: 'auto',
            background: 'var(--surface-inverse)',
            color: 'var(--text-inverse)',
            border: 'none',
            borderRadius: '9999px',
            width: '32px',
            height: '32px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            padding: 0,
            flexShrink: 0
          }}
        >
          <FaTimes size={16} color="var(--text-inverse)" />
        </button>
      )}
    </div>
  );
}

export default IncomingCallPill;
